import styled from 'styled-components'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import Button from '@mui/material/Button'
import { useSelector } from 'react-redux'
import { selectCourse } from 'store/course/slice'
import { useIntl } from 'react-intl'
import LoadingSpinner from 'components/ui/LoadingSpinner'

interface Props {
  handleSubmit: () => void
  close: () => void
}

export default function ConfirmGpsSaveDialog({ handleSubmit, close }: Props): JSX.Element {
  const intl = useIntl()
  const { gpsVersionStatus, gpsVersionZendeskStatus } = useSelector(selectCourse)

  const status = gpsVersionStatus !== 'succeeded' ? gpsVersionStatus : gpsVersionZendeskStatus
  const isLoading = status === 'loading'

  return (
    <StyledDialog open onClose={close} hideBackdrop>
      <StyledDialogContent>
        <Title>{intl.formatMessage({ id: 'strings.sendGpsDataDialogTitle' })}</Title>
        <ContentText>{intl.formatMessage({ id: 'strings.sendGpsDataDialogText' })}</ContentText>
      </StyledDialogContent>
      <StyledDialogActions>
        <SecondaryButton onClick={close} disabled={isLoading}>
          {intl.formatMessage({ id: 'strings.continueEditing' })}
        </SecondaryButton>
        <PrimaryButton onClick={() => handleSubmit()} disabled={isLoading}>
          {isLoading ? <LoadingSpinner size={20} /> : intl.formatMessage({ id: 'strings.sendGpsData' })}
        </PrimaryButton>
      </StyledDialogActions>
    </StyledDialog>
  )
}

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    width: min(480px, calc(100vw - 32px));
    border-radius: 16px;
    border-top: 0.5px solid rgba(255, 255, 255, 0.15);
    border-bottom: 0.5px solid rgba(255, 255, 255, 0.15);
    background: rgba(20, 24, 26, 0.7);
    backdrop-filter: blur(5px);
    color: #fff;
  }
`

const StyledDialogContent = styled(DialogContent)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
`

const StyledDialogActions = styled(DialogActions)`
  justify-content: space-between;
  gap: 12px;
`

const Title = styled.h2`
  margin: 0;
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
`

const ContentText = styled(DialogContentText)`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.4;
`

const DialogButton = styled(Button)`
  height: 34px;
  border-radius: 12px;
  text-transform: none;
  font-size: 16px;
  font-weight: 500;
  padding: 8px 16px;
  min-width: unset;
`

const PrimaryButton = styled(DialogButton)`
  background: transparent;
  color: #fff;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  &.Mui-disabled {
    color: rgba(255, 255, 255, 0.7);
  }
`

const SecondaryButton = styled(DialogButton)`
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  &.Mui-disabled {
    color: rgba(255, 255, 255, 0.4);
  }
`
